// App shell (DESIGN §2): top bar (firm, search / command palette, inbox badge, notifications, engine heartbeat, me) and the
// collapsible left rail. Clients get a reduced shell with only their portal.
import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Icon, Kbd, Popover, StatusPill, type IconName } from "../components";
import { ROLE_RANK } from "../data/source";
import type { EngagementHeader } from "../data/types";
import { fmtDate, fmtStamp, titleCase } from "../lib/format";
import { stageColumn, typeLabel } from "../lib/workspace";
import { useLoad, usePref, useRealtime } from "../lib/hooks";
import { CommandPalette } from "./CommandPalette";
import { useApp } from "./context";
import { useDecisions } from "./decisions";
import { EngineStatus } from "./EngineStatus";
import { ENGAGEMENT_SECTIONS, RAIL, type RailItem } from "./nav";
import { useTheme, type ThemeChoice } from "./theme";

export function AppShell({ children }: { children: ReactNode }) {
  const { me } = useApp();
  const [collapsed, setCollapsed] = usePref("rail-collapsed", false);
  const [mobileRail, setMobileRail] = useState(false);
  const [palette, setPalette] = useState(false);
  const { pathname } = useLocation();
  useTheme();
  useEffect(() => setMobileRail(false), [pathname]);
  useEffect(() => {
    if (me.role === "client") return;
    const on = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      const typing = el?.tagName === "INPUT" || el?.tagName === "TEXTAREA" || el?.tagName === "SELECT" || el?.isContentEditable;
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setPalette(true); }
      else if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey) { e.preventDefault(); setPalette(true); }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [me.role]);

  return (
    <div className="flex h-screen flex-col">
      <a href="#main" className="sr-only z-50 rounded bg-brand px-3 py-2 text-brand-ink focus:not-sr-only focus:absolute focus:left-2 focus:top-2">Skip to content</a>
      <TopBar onPalette={() => setPalette(true)} onMenu={() => setMobileRail((o) => !o)} menuOpen={mobileRail} />
      <div className="relative flex min-h-0 flex-1">
        {me.role !== "client" && (
          <>
            {mobileRail && <div className="fixed inset-0 z-20 bg-black/40 md:hidden" aria-hidden="true" onClick={() => setMobileRail(false)} />}
            <Rail collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} mobileOpen={mobileRail} />
          </>
        )}
        <main id="main" tabIndex={-1} className="relative min-w-0 flex-1 overflow-y-auto px-4 py-4 focus-visible:outline-none md:px-6 md:py-5">{children}</main>
      </div>
      {me.role !== "client" && palette && <CommandPalette open onClose={() => setPalette(false)} />}
    </div>
  );
}

function TopBar({ onPalette, onMenu, menuOpen }: { onPalette: () => void; onMenu: () => void; menuOpen: boolean }) {
  const { me } = useApp();
  const staff = me.role !== "client";
  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-raised px-2 md:px-3">
      {staff && (
        <button type="button" onClick={onMenu} aria-label="Menu" aria-expanded={menuOpen} className="rounded p-1.5 hover:bg-sunken md:hidden"><Icon name="menu" /></button>
      )}
      <Link to={staff ? "/" : "/portal"} className="flex shrink-0 items-center gap-2 rounded px-1 font-semibold">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded bg-brand text-xs font-bold text-brand-ink" aria-hidden="true">YVC</span>
        <span className="hidden text-sm sm:inline">Your Virtual CPA</span>
      </Link>
      {staff && (
        <button type="button" onClick={onPalette} aria-label="Search or run a command (Control K)" aria-keyshortcuts="Control+K Meta+K /"
          className="mx-auto flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md border border-line bg-paper px-2 text-sm text-muted hover:border-brand md:max-w-md">
          <Icon name="search" size={16} className="shrink-0" /><span className="min-w-0 flex-1 truncate text-left">Search or run a command</span><span className="hidden sm:inline"><Kbd>⌘K</Kbd></span>
        </button>
      )}
      <div className={`flex shrink-0 items-center gap-0.5 ${staff ? "" : "ml-auto"}`}>
        {staff && <EngineStatus />}
        {staff && <InboxBadge />}
        {staff && <Notifications />}
        <MeMenu />
      </div>
    </header>
  );
}

function InboxBadge() {
  const { open } = useDecisions();
  const n = open.length;
  return (
    <NavLink to="/inbox" aria-label={`Inbox, ${n} decision${n === 1 ? "" : "s"} waiting`} className="relative rounded p-1.5 hover:bg-sunken">
      <Icon name="inbox" />
      {n > 0 && <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-blocked px-1 text-center text-[10px] font-bold leading-4 text-paper">{n}</span>}
    </NavLink>
  );
}

/** Notifications = what became of this person's own requests (the engine's outcomes), newest first. */
function Notifications() {
  const { ds } = useApp();
  const mine = useLoad(() => ds.inbox(), [ds]);
  useRealtime(ds, "inbox", mine.reload);
  const rows = (mine.data ?? []).slice(0, 8);
  const unseen = rows.filter((r) => r.status === "refused" || r.status === "failed").length;
  return (
    <Popover label="Notifications" button={({ open, toggle }) => (
      <button type="button" onClick={toggle} aria-expanded={open} aria-label={`Notifications${unseen ? `, ${unseen} refused` : ""}`} className="relative rounded p-1.5 hover:bg-sunken">
        <Icon name="bell" />
        {unseen > 0 && <span className="absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-blocked" />}
      </button>
    )}>
      {() => (
        <div>
          <p className="px-2 pb-1 text-xs font-semibold text-muted">Your requests to the engine</p>
          {rows.length === 0 ? <p className="px-2 py-4 text-sm text-muted">Nothing sent yet.</p> : (
            <ul className="divide-y divide-line">
              {rows.map((r) => (
                <li key={r.id} className="space-y-0.5 px-2 py-1.5 text-sm">
                  <div className="flex items-center justify-between gap-2"><span className="font-mono text-xs">{r.command}</span><StatusPill state={r.status} /></div>
                  {r.refusal && <p className="text-xs text-blocked">{r.refusal}</p>}
                  <p className="text-[11px] text-muted">{fmtStamp(r.createdAt)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Popover>
  );
}

function MeMenu() {
  const { me, ds, signOut, switchRole } = useApp();
  const [theme, setTheme] = useTheme();
  return (
    <Popover label="Account" button={({ open, toggle }) => (
      <button type="button" onClick={toggle} aria-expanded={open} aria-label={`Account: ${me.personName}, ${me.role}`} className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-sunken">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sunken text-xs font-semibold ring-1 ring-line">{me.personName.split(" ").map((p) => p[0]).join("").slice(0, 2)}</span>
        <span className="hidden text-left text-xs leading-tight lg:block"><span className="block font-medium">{me.personName}</span><span className="text-muted">{titleCase(me.role)}</span></span>
      </button>
    )}>
      {(close) => (
        <div className="space-y-3 p-1 text-sm">
          <div><p className="font-medium">{me.personName}</p><p className="text-xs text-muted">{titleCase(me.role)}{me.email ? ` · ${me.email}` : ""}{ds.mode === "demo" ? " · demo data" : ""}</p></div>
          <fieldset>
            <legend className="mb-1 text-xs font-semibold text-muted">Theme</legend>
            <div className="flex gap-1">
              {(["system", "light", "dark"] as ThemeChoice[]).map((t) => (
                <button key={t} type="button" aria-pressed={theme === t} onClick={() => setTheme(t)}
                  className={`rounded border px-2 py-0.5 text-xs ${theme === t ? "border-brand bg-brand/10 text-brand" : "border-line"}`}>{titleCase(t)}</button>
              ))}
            </div>
          </fieldset>
          {switchRole && (
            <fieldset>
              <legend className="mb-1 text-xs font-semibold text-muted">Demo: view as</legend>
              <div className="flex flex-wrap gap-1">
                {(["partner", "manager", "staff", "client"] as const).map((r) => (
                  <button key={r} type="button" aria-pressed={me.role === r} onClick={() => { close(); switchRole(r); }}
                    className={`rounded border px-2 py-0.5 text-xs ${me.role === r ? "border-brand bg-brand/10 text-brand" : "border-line"}`}>{titleCase(r)}</button>
                ))}
              </div>
            </fieldset>
          )}
          <button type="button" onClick={() => { close(); signOut(); }} className="w-full rounded border border-line px-2 py-1 text-left hover:bg-sunken">Sign out</button>
        </div>
      )}
    </Popover>
  );
}

/** The left rail. Inside an engagement (/e/:id): the engagement switcher and its six sections; elsewhere: Home, Clients,
 * Action center. Everything else lives in one "Firm" menu (collapsed by default, remembered per viewer). */
function Rail({ collapsed, onToggle, mobileOpen }: { collapsed: boolean; onToggle: () => void; mobileOpen: boolean }) {
  const { me } = useApp();
  const { pathname } = useLocation();
  const eng = pathname.match(/^\/e\/([^/]+)/)?.[1];
  const firmActive = RAIL.some((r) => r.group === "firm" && r.to !== "/" && pathname.startsWith(r.to));
  const [firmOpen, setFirmOpen] = usePref("firm-menu-open", false);
  const showFirm = firmOpen || firmActive;
  const link = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm ${isActive ? "bg-brand/10 font-semibold text-brand" : "text-ink hover:bg-sunken"}`;
  const item = (to: string, label: string, icon: IconName, opts: { end?: boolean; minRole?: RailItem["minRole"] } = {}) => {
    const locked = opts.minRole && ROLE_RANK[me.role] < ROLE_RANK[opts.minRole];
    return (
      <li key={to}>
        <NavLink to={to} end={opts.end} className={link} title={collapsed ? label : undefined} aria-label={collapsed ? label : undefined}>
          <Icon name={icon} />
          {!collapsed && <span className="flex-1 truncate">{label}</span>}
          {!collapsed && locked && <span className="text-[10px] text-muted" title={`${titleCase(opts.minRole!)}s only`}>{titleCase(opts.minRole!)}s</span>}
        </NavLink>
      </li>
    );
  };
  return (
    <nav aria-label="Main"
      className={`${mobileOpen ? "fixed inset-y-12 left-0 z-30 flex w-64" : "hidden"} shrink-0 flex-col overflow-y-auto border-r border-line bg-raised md:static md:flex ${collapsed ? "md:w-14" : "md:w-60"}`}>
      {eng ? (
        <>
          {!collapsed && <EngagementSwitcher id={decodeURIComponent(eng)} />}
          <ul className="space-y-0.5 p-2" aria-label="This engagement">
            {ENGAGEMENT_SECTIONS.map((s) => item(`/e/${eng}${s.path ? "/" + s.path : ""}`, s.label, s.icon, { end: !s.path }))}
          </ul>
          <div className="mx-3 border-t border-line" />
          <ul className="space-y-0.5 p-2">{item("/", "All engagements", "briefcase", { end: true })}</ul>
        </>
      ) : (
        <ul className="space-y-0.5 p-2">{RAIL.filter((r) => !r.group).map((r) => item(r.to, r.label, r.icon, { end: r.to === "/", minRole: r.minRole }))}</ul>
      )}
      <div className="px-2">
        <button type="button" onClick={() => setFirmOpen(!showFirm)} aria-expanded={showFirm} aria-controls="firm-menu" aria-label={collapsed ? "Firm menu" : undefined}
          className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm text-muted hover:bg-sunken hover:text-ink">
          <Icon name="layers" />{!collapsed && <span className="flex-1 font-semibold">Firm</span>}{!collapsed && <Icon name={showFirm ? "chevronDown" : "chevronRight"} size={14} />}
        </button>
      </div>
      {showFirm && <ul id="firm-menu" className="space-y-0.5 p-2 pt-1">{RAIL.filter((r) => r.group === "firm").map((r) => item(r.to, r.label, r.icon, { minRole: r.minRole }))}</ul>}
      <button type="button" onClick={onToggle} aria-label={collapsed ? "Expand the menu" : "Collapse the menu"} aria-expanded={!collapsed}
        className="mt-auto hidden items-center gap-2 border-t border-line px-4 py-2 text-xs text-muted hover:text-ink md:flex">
        <Icon name={collapsed ? "chevronRight" : "chevronLeft"} size={16} />{!collapsed && "Collapse"}
      </button>
    </nav>
  );
}

/** Top of the rail inside an engagement: which engagement this is, and a switch to any other one you can see. */
function EngagementSwitcher({ id }: { id: string }) {
  const { ds } = useApp();
  const es = useLoad(() => ds.engagements(), [ds]);
  const nav = useNavigate();
  const { pathname } = useLocation();
  const here = es.data?.find((e) => e.id === id);
  const section = pathname.replace(/^\/e\/[^/]+/, "").match(/^\/(folders|workpapers|documents|actions|activity)/)?.[0] ?? "";
  const line = (e: EngagementHeader) => `FYE ${fmtDate(e.fye)} · ${typeLabel(e.engagementType)} · ${stageColumn(e)}`;
  return (
    <div className="border-b border-line p-2">
      <Popover label="Switch engagement" align="left" button={({ open, toggle }) => (
        <button type="button" onClick={toggle} aria-expanded={open} aria-label={`Engagement: ${here?.clientName ?? id}. Switch engagement`}
          className="flex w-full items-start gap-2 rounded-md border border-line bg-paper px-2.5 py-2 text-left hover:border-brand">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold">{here?.clientName ?? id}</span>
            <span className="block truncate text-[11px] text-muted">{here ? line(here) : "Loading…"}</span>
          </span>
          <Icon name="chevronDown" size={16} className="mt-0.5 shrink-0 text-muted" />
        </button>
      )}>
        {(close) => (
          <ul className="max-h-80 space-y-0.5 overflow-y-auto" aria-label="Engagements">
            {(es.data ?? []).map((e) => (
              <li key={e.id}>
                <button type="button" aria-current={e.id === id ? "true" : undefined} onClick={() => { close(); nav(`/e/${e.id}${section}`); }}
                  className={`w-full rounded px-2 py-1.5 text-left hover:bg-sunken ${e.id === id ? "bg-brand/10" : ""}`}>
                  <span className="block text-sm font-medium">{e.clientName}</span>
                  <span className="block text-[11px] text-muted">{line(e)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Popover>
    </div>
  );
}
